import type { FromWhere } from 'src/components/root/declare'
import type { ButtonProps } from 'src/components/root/components/buttons/declare'
import type { FilterGroup } from 'src/utils/common'
import { InfoCircleOutlined } from '@ant-design/icons'
import { LoadableControl, LoadableButton, EasyModal, EasyImg, FormPage, EditPage, PasswordLabel } from 'src/components'
import { appStore, listStore, dynamicJsStore } from 'src/stores'
import {
  getMenuFormParams,
  getValidNavColumns,
  getValidBtns,
  checkCondition,
  getNavFieldsFromNavColumns,
  getDefaultValueFrom_DEFAULT_VALUE,
} from 'src/components/root/helper'
import { initFilter, getSingleRule, extendRules, extendRulesAndGroups } from 'src/utils/common'
import { $api, $consts } from 'src/plugins'
import { getImgBase } from 'src/utils/axios'
import { pictureThumb } from 'src/assets/base64'
import { get, set } from 'lodash'
import dayjs from 'dayjs'

/**
 * 获取可编辑的列表表单的控件
 * 不在此列举之内的则单纯渲染显示值
 */
export const getEditListTableControlType = () => {
  return [
    // 文本输入框
    { type: $consts['TABLE/CONTROL_TYPE_TEXTINPUT'] },
    { type: $consts['TABLE/CONTROL_TYPE_TEXTAREA'] },
    // 时间控件类型
    { type: $consts['TABLE/CONTROL_TYPE_DATE_DAY_SECOND'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_SECOND'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_MINUTE'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_DAY'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_MONTH'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_RANGE_MONTH'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_YEAR'] },
    { type: $consts['TABLE/CONTROL_TYPE_DATE_RANGE_YEAR'] },
    // 枚举下拉单选框
    { type: $consts['TABLE/CONTROL_TYPE_ENUM_SELECT'] },
    // 复选框控件
    { type: $consts['TABLE/CONTROL_TYPE_CHECKBOX'] },
    // 分页下拉
    { type: $consts['TABLE/CONTROL_TYPE_PAGINATION_SELECT'] },
    // 数字输入框
    { type: $consts['TABLE/CONTROL_TYPE_NUMBERINPUT'] },
    { type: $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_TWODOT'] },
    { type: $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_THREEDOT'] },
    // 图片/文件选择
    { type: $consts['TABLE/CONTROL_TYPE_FILE_SELECT'] },
  ]
}

/**
 * 依据 Table 列配置的 FORMAT 格式化函数，格式化 table cell
 * @param value 
 * @param format 
 * @returns 
 */
export const formatCellText = (value: any, format: string) => {
  if (!value) return value

  switch (format) {
    // 为0的显示为空
    case 'colNullFormat':
      return parseFloat(value) === 0 ? '' : value
    // boolean 值格式化
    case 'colBooleanFormat':
      {
        const str = String(value).toLowerCase()
        return str === 'true' || str === '1' ? '是' : '否'
      }
    // 日期时间
    case 'colDateTimeFormat':
      return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
    // 日期
    case 'colDateFormat':
      return dayjs(value).format('YYYY-MM-DD')
    // 时间
    case 'colTimeFormat':
      return dayjs(value).format('HH:mm:ss')
    // 一位小数
    case 'colDigitsFormat_1':
      return Number(value.toFixed(1))
    // 两位小数
    case 'colDigitsFormat_2':
      return Number(value.toFixed(2))
    // 三位小数
    case 'colDigitsFormat_3':
      return Number(value.toFixed(3))
    // 四位小数
    case 'colDigitsFormat_4':
      return Number(value.toFixed(4))
    default:
      return value
  }
}

type CellOptions = {
  record: any,
  config: any,
  enums?: any[]
}
/**
 * ListTable cell 的显示值
 * @param options 
 * @returns 
 */
export const renderTableCell = (options: CellOptions) => {
  const getTableCell = () => {
    const baseConfig = appStore.current.baseConfig ?? {}

    const { record, config = {}, enums = [] } = options
    const { FIELD_NAME, CONTROL_TYPE, ENUM, FORMAT } = config
    const cell = get(record, FIELD_NAME)

    // 配置了格式化函数
    if (FORMAT) {
      return formatCellText(cell, FORMAT)
    }
    // 是个数组值
    if (Array.isArray(cell)) {
      return cell.join(';')
    }
    // 是个枚举值
    if (ENUM) {
      const options = enums.find((item: any) => item.EnumName === ENUM)?.options
      // eslint-disable-next-line eqeqeq
      const find = (options || []).find((item: any) => item.value == cell)
      return find?.label || cell
    }
    // 是个普通文本
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_TEXT']) {
      return <span dangerouslySetInnerHTML={{ __html: cell }} />
    }
    // 是个bool文本
    if (
      CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_BOOL_TEXT'] ||
      typeof cell === 'boolean'
    ) {
      return cell ? '是' : '否'
    }
    // 是个密码值
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_PASSWORD']) {
      return <PasswordLabel text={cell} letterCounts={6} />
    }
    // 是个上传文件
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_FILE_UPLOAD']) {
      let files = []
      try { files = JSON.parse(cell) } catch (err) { }
      return files.map((item: any) => {
        const url = `${baseConfig.FileDomain || getImgBase()}${item.FilePath}`
        return (
          <EasyImg
            popEnable
            defaultSrc={pictureThumb}
            src={url}
            popStyle={{ width: 180, height: 180 }}
            style={{ width: 22, height: 22, verticalAlign: 'text-top' }}
          />
        )
      })
    }
    // 是个图片/文件
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_FILE_SELECT']) {
      return <EasyImg src={cell} style={{ width: 19, height: 19, verticalAlign: 'text-top' }} />
    }
    // DATE 时分秒选择：精度到秒
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_DATE_SECOND']) {
      // 兼容 cell 值为 18:00:00 的情况
      if (dayjs(cell, 'HH:mm:ss', true).isValid()) {
        return dayjs(cell, 'HH:mm:ss', true).format('HH:mm:ss')
      }
      return dayjs(cell).format('HH:mm:ss')
    }
    // DATE 日期选择：精度到日
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_DATE_DAY']) {
      return dayjs(cell).format('YYYY-MM-DD')
    }
    // DATE 月份选择：精度到月份
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_DATE_MONTH']) {
      return dayjs(cell).format('YYYY-MM')
    }
    // DATE 年度选择：精度到年份
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_DATE_YEAR']) {
      return dayjs(cell).format('YYYY')
    }
    // 数字输入框：两位小数精度
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_TWODOT']) {
      return Number(Number(cell).toFixed(2))
    }
    // 数字输入框：三位小数精度
    if (CONTROL_TYPE === $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_THREEDOT']) {
      return Number(Number(cell).toFixed(3))
    }
    // 是个空值
    return cell === undefined ? '' : cell
  }

  const { record, config = {} } = options
  const { SHOW_FORM_CODE, SHOW_ID_FIELD } = config

  // 列配置了需要额外显示的表单
  if (SHOW_FORM_CODE && SHOW_ID_FIELD) {
    const formId = get(record, SHOW_ID_FIELD)
    return (
      <div onClick={evt => evt.stopPropagation()} className='display-flex flex-rowfsc'>
        {getTableCell()}
        <EasyModal
          modal={{ width: '100%' }}
          slot={<InfoCircleOutlined className='fz-16 pd-l4' />}
          slotStyle={{ verticalAlign: 'text-bottom' }}
        >
          <FormPage formCode={SHOW_FORM_CODE} formData={{ formId, formDisabled: true }} />
        </EasyModal>
      </div>
    )
  }

  return getTableCell()
}

type PageOption = {
  PageIndex: number,
  Limit: number,
  Sort?: string,
  Order?: number
}
type TableConfig = {
  __EDIT_V1__: boolean, // FORM_TYPE_EDITLIST_PAGE 可编辑列表表单
  __EDIT_V2__: boolean, // FORM_TYPE_DYNAMIC_EDITLIST_PAGE 可编辑动态列列表表单
  Nav_Form: any, // 表单配置
  Nav_Columns: any[], // table columns
  PageTable: any, // table 配置
  Btns: any[], // table 按钮
  RowBtns: any[], // table row 行按钮
  TableParams: any, // table dataSource 过滤参数
  [propsName: string]: any
}
type TableParams = {
  tableConfig: TableConfig, // table 配置
  pageOption: PageOption, // 分页请求参数
  searchFilterGroup?: FilterGroup, // 搜索请求参数
  filterGroup?: FilterGroup, // 额外的 table 请求参数
  fromWhere?: FromWhere
}

/**
 * 根据表格操作行按钮生成对应的 columns
 * @param {Array} btns table row 行按钮
 * @returns 
 */
export const getTableOperateColumns = (
  btns: Array<any>,
  btnConfig?: Partial<Omit<ButtonProps, 'config'>> & { config?: Partial<ButtonProps['config']> }
): Array<any> => {
  if (!Array.isArray(btns) || !btns.length) return []

  // 检查按钮权限
  const validBtns = getValidBtns(btns).sort((a, b) => a.NUM - b.NUM)

  return [{
    fixed: 'right',
    title: <span className='operate-column__title fz-14'>操作</span>,
    key: $consts['TABLE/LIST_TABLE_COLUMN_OPERATE'],
    dataIndex: $consts['TABLE/LIST_TABLE_COLUMN_OPERATE'],
    ellipsis: true,
    className: 'operate-column',
    render: (text: any, record: any) => {
      // 检查按钮显示条件
      const showBtns: any[] = []
      validBtns.forEach((btn: any) => {
        const isShow = checkCondition(btn.BTN_CONDITION, record, appStore.current.activeMenu.MENU_FORM_PARAMS)
        isShow && showBtns.push(btn)
      })
      return showBtns.map(btn => {
        const btnProps = {
          pageType: $consts['TABLE/FORM_TYPE_LIST_PAGE'],
          locateBtn: $consts['TABLE/BTN_LOCATE_ROW'],
          ...btnConfig,
          config: { ...btn, __RECORD__: record, ...btnConfig?.config }
        }
        return (
          <span key={btn.ID} className='operate-column__btn'>
            <LoadableButton {...btnProps} />
          </span>
        )
      })
    }
  }]
}

/**
 * 获取 table dataSource
 * @param {TableConfig} params 
 * @returns {Promise<{ total: number, columns: any[], dataSource: any[] }>}
 */
export const getTableData = (params: TableParams) => {
  /**
   * 获取 table columns
   * @param {Object} option
   * @param {Array}  option.Nav_Columns table columns
   * @param {Object} option.PageTable table 配置
   * @param {Array}  option.RowBtns table row 行按钮
   * @param {Array}  option.Nav_Enums table 列枚举数据
   */
  const getTableColumns = ({
    Nav_Form = {},
    Nav_Columns = [],
    PageTable,
    RowBtns,
    Nav_Enums: enums = []
  }: TableConfig, fromWhere?: FromWhere) => {
    const columns = []
    if (PageTable.IS_SHOW_ROW_NO) {
      columns.push({
        title: '序号',
        key: $consts['TABLE/LIST_TABLE_COLUMN_NO'],
        dataIndex: $consts['TABLE/LIST_TABLE_COLUMN_NO'],
        ellipsis: true,
        render: (text: number) => {
          return <span style={{ padding: '0 10px' }}>{text}</span>
        }
      })
    }
    getValidNavColumns(Nav_Columns).forEach((col: any) => {
      const { LABEL, FIELD_NAME, CONTROL_TYPE, ON_RENDER, ON_CELL, FORMAT, NO_SORT } = col
      // 渲染配置的自定义函数
      const { JS_FILES } = Nav_Form
      const funcs = dynamicJsStore.dynamicJs[JS_FILES?.split('.')[0]]
      const customRender = (ON_RENDER || FORMAT) && funcs?.[ON_RENDER || FORMAT]
      // 渲染可编辑控件
      const controlRender = !!getEditListTableControlType().find(item => item.type === CONTROL_TYPE)

      columns.push({
        config: col,
        title: LABEL,
        key: FIELD_NAME,
        dataIndex: FIELD_NAME,
        sorter: !NO_SORT,
        showSorterTooltip: false,
        ellipsis: true,
        onCell: ON_CELL && funcs?.[ON_CELL] ? (record: any, index: number) => funcs[ON_CELL](record, index) : undefined,
        render: (text: any, record: any) => {
          // 渲染可编辑控件
          if (controlRender && record.__EDITABLE__) {
            return (
              <LoadableControl
                pageType={$consts['TABLE/FORM_TYPE_LIST_PAGE']}
                locateControl={$consts['TABLE/CONTROL_LOCATE_ROW']}
                config={{ ...col, __RECORD__: record }}
              />
            )
          }
          // 渲染配置的自定义函数
          if (!!customRender) return customRender(text, record, listStore.table.get(Nav_Form.CODE))
          // 单纯渲染值
          const cell = renderTableCell({ record, config: col, enums })
          if (col.WIDTH) {
            const style = { width: col.WIDTH }
            col.LINE_BREAK && set(style, 'whiteSpace', 'normal')
            return <div className='text-ellipsis' style={style}>{cell}</div>
          }
          return cell
        }
      })
    })

    // 批量保存按钮弹窗列表
    const matchListHeader = (
      fromWhere?.pageType === $consts['TABLE/FORM_TYPE_LIST_PAGE']
      && fromWhere?.locateType === $consts['TABLE/BTN_LOCATE_HEADER']
      && fromWhere?.funcType === $consts['TABLE/FUNC_TYPE_BATCH_SAVE']
      && fromWhere?.funcData
    )

    return columns.concat(matchListHeader ? [] : getTableOperateColumns(RowBtns))
    // 操作栏不显示操作按钮
    // return columns
  }

  /**
   * 兼容旧的可编辑列表表单
   * 获取可编辑列表表单 columns 数据
   * @param columns 
   */
  const generateTableColumnsV1 = (columns: any[] = []) => {
    if (!Array.isArray(columns) || !columns.length) return

    columns.forEach(item => {
      const { dataIndex, children } = item
      if (Array.isArray(children) && children.length) {
        generateTableColumnsV1(children)
      } else {
        delete item.children
        item.ellipsis = true
        item.onCell = (record: any) => {
          const { colSpan, rowSpan } = get(record, dataIndex) ?? {}
          return { colSpan, rowSpan }
        }
        item.render = (text: any, record: any) => {
          const loadableConfig: any = {
            __RECORD__: record,
            FIELD_NAME: `${dataIndex}.value`,
            CONTROL_TYPE: text.controlType,
          }
          if (text.disabled) return text.value
          return (
            <LoadableControl
              pageType={$consts['TABLE/FORM_TYPE_LIST_PAGE']}
              locateControl={$consts['TABLE/CONTROL_LOCATE_ROW']}
              config={loadableConfig}
            />
          )
        }
      }
    })
  }

  /**
   * 兼容旧的可编辑列表表单
   * 获取可编辑列表表单 dataSource 数据
   * @param data 
   */
  const getTableDataSourceV1 = (data: any[] = []) => {
    return data.map((item, index) => {
      const source: any = { ID: index }
      item.forEach((field: any) => {
        const { fieldName, ...rest } = field
        source[fieldName] = { ...rest }
      })
      return source
    })
  }

  /**
   * 获取 table dataSource 请求参数
   */
  const getTableLoadPayload = () => {
    const { Nav_Columns = [], PageTable, TableParams } = params.tableConfig
    let {
      SORT: Sort = 'CREATE_TIME',
      SORTS,
      ORDER: Order = PageTable.SORT ? PageTable.ORDER : 1,
      ORDERS,
      NO_SELECT_FIELD,
      FILTER_ORG_TYPE: OrgType
    } = PageTable
    // 存在列表表头排序则表头排序优先
    const { PageIndex, Limit, Sort: PSort, Order: POrder } = params.pageOption
    Sort = PSort || Sort
    Order = POrder !== undefined ? POrder : Order

    // 存在多字段排序
    const Orders: { Field: string, Order: number }[] = []
    if (!PSort && SORTS) {
      const sorts = SORTS.split(',')
      const orders = ORDERS?.split(',') ?? []
      sorts.forEach((field: string, index: number) => {
        Orders.push({ Field: field, Order: Number(orders[index] ?? 0) })
      })
    }

    // SelectField 参数
    const SelectField = Nav_Columns.map((item: any) => item.FIELD_NAME).filter((item: any) => item && item !== 'null')
    if (SelectField.length && !NO_SELECT_FIELD) {
      SelectField.indexOf('ID') === -1 && SelectField.push('ID')
      SelectField.indexOf('ORG_ID') === -1 && SelectField.push('ORG_ID')
    }

    // Include 参数
    const Include = getNavFieldsFromNavColumns(Nav_Columns)

    // payload 请求参数
    const { OrgId } = appStore.current.headers
    const payload: any = initFilter({ OrgId, OrgType, Sort, Order, PageIndex, Limit, Include, Orders, SelectField })

    // 过滤参数
    const filterRules: any = []
    if (Array.isArray(TableParams) && TableParams.length) {
      TableParams.forEach(item => {
        filterRules.push({
          Field: item.FIELD_NAME,
          Operate: item.OPERATION,
          Value: getDefaultValueFrom_DEFAULT_VALUE(item.VALUE, {
            user: appStore.current.userinfo,
          }),
          IsCustom: item.IS_CUSTOM,
          IsSysParamRule: item.IS_SYS_PARAM,
        })
      })
    }
    filterRules.length && extendRulesAndGroups(payload, { Rules: filterRules })
    // 菜单参数
    payload.MenuParameter = appStore.current.activeMenu.MENU_FORM_PARAMS
    // 表单参数
    const formParams = getMenuFormParams(appStore.current.activeMenu)
    for (const [key, value] of Object.entries(formParams)) {
      payload[key] = value
    }
    // ListSearch 搜索参数
    params.searchFilterGroup && extendRulesAndGroups(payload, params.searchFilterGroup)
    // 额外请求参数
    params.filterGroup && extendRulesAndGroups(payload, params.filterGroup)

    return payload
  }

  return new Promise((resolve) => {
    const payload = getTableLoadPayload()
    const { __EDIT_V1__, __EDIT_V2__, PageTable } = params.tableConfig
    let total = 0
    let columns: any[] = []
    let dataSource: any[] = []
    let copyTableConfig = { ...params.tableConfig }
    const funcs = [
      $api['app/getDataByPost'](payload, {
        url: PageTable.API_URL,
        fullData: true
      })
    ]
    if (PageTable.COLUMN_API_URL) {
      funcs.push(
        $api['app/getDataByPost'](payload, {
          url: PageTable.COLUMN_API_URL,
        }).catch(() => null)
      )
    }
    Promise.all(funcs).then((datas: any[]) => {
      const [resData, resColumn] = datas
      total = resData?.TotalCount ?? 0
      // 可编辑的列表（兼容旧的可编辑列表表单）
      if (__EDIT_V1__) {
        generateTableColumnsV1(resData.Data?.columns)
        columns = resData.Data?.columns || []
        dataSource = getTableDataSourceV1(resData.Data?.data)
      } else {
        // 可编辑动态列列表表单
        if (__EDIT_V2__ && resColumn?.length) {
          Object.assign(copyTableConfig, { Nav_Columns: resColumn })
        }
        // 列表表单
        if (resData?.Data?.length) {
          PageTable.IS_SHOW_ROW_NO && resData.Data.forEach((item: any, index: number) => {
            item[$consts['TABLE/LIST_TABLE_COLUMN_NO']] = index + 1
          })
          dataSource = resData.Data
        }
      }
    }).finally(() => {
      resolve({
        total,
        columns: __EDIT_V1__ ? columns : getTableColumns(copyTableConfig, params.fromWhere),
        dataSource,
      })
    })
  })
}

/**
 * （旧版本）获取列表表单对应搜索和表格配置
 * @param formCode 
 * @param filter 其他过滤条件
 * @returns 
 */
export const getTablesConfig_v1 = (formCode: string, filter?: any) => {
  const { OrgId, UserId } = appStore.current.headers
  const payload = initFilter({ OrgId, Keyword: formCode, ...filter })

  return Promise.all([
    $api['table/getSearchConfigInfo'](payload),
    $api['table/getTablePageTables'](payload),
  ]).then(datas => {
    const promiseFunc = (table: any) => {
      const payload = initFilter({ OrgId, Sort: 'NUM', Parameter1: UserId })
      extendRules(payload, [getSingleRule('ID', 1, table.ID)])
      return $api['table/getTablePageConfigInfo'](payload)
    }

    const [search, table = { Nav_Tables: [] }] = datas
    return Promise.all(
      table.Nav_Tables.map((table: any) => promiseFunc(table))
    ).then(tables => {
      const validTables = tables.filter(item => !!item)
      // 查找所有的枚举
      const allEnums: string[] = []
      validTables.forEach(table => {
        allEnums.push(...table.Nav_Columns.map((item: any) => item.ENUM).filter((item: any) => item))
      })
      // 存在枚举，则将枚举值赋值在表格配置中
      if (allEnums.length) {
        return appStore.getEnums(allEnums).then(data => {
          return validTables.map(item => ({ ...search, ...item, Nav_Enums: data }))
        })
      }
      return validTables.map(item => ({ ...search, ...item }))
    })
  })
}

/**
 * 获取列表行是否有配置编辑按钮
 * @param tableConfig 
 * @param record 
 */
export const getRowEditBtn = (tableConfig: TableConfig, record: any) => {
  const btns = tableConfig.RowBtns
  if (!Array.isArray(btns) || !btns.length) return <span />

  // 检查按钮权限
  const validBtns = getValidBtns(btns)
  // 检查按钮显示条件
  const showBtns: any[] = []
  validBtns.forEach((btn: any) => {
    const isShow = checkCondition(btn.BTN_CONDITION, record, appStore.current.activeMenu.MENU_FORM_PARAMS)
    isShow && showBtns.push(btn)
  })
  const editBtn = showBtns.find(btn => btn.BTN_TYPE === $consts['TABLE/BTN_TYPE_EDIT_PAGE'])

  // 存在编辑按钮则点击弹窗
  if (!editBtn) return <span />

  const { FORM_CODE } = editBtn
  return (
    <EasyModal
      modal={{
        title: record?.NAME || record?.ALIAS || FORM_CODE,
        width: '100%'
      }}
      visible={true}
      slot={null}
    >
      <EditPage formCode={FORM_CODE} formData={{ formId: record.ID }} />
    </EasyModal>
  )
}